import { JSX, useMemo, useState } from "react";
import DownloadOptionsMenu, {
  type WidgetDownloadOptions,
} from "./DownloadOptionsMenu";

type DataRow = Record<string, unknown>;

type InfoBarWidgetProps = {
  isLoading: boolean;
  data: unknown;
  title?: string;
  dashboardName?: string;
  widgetTemplateId?: string | number;
  appliedFilters?: Record<string, unknown>;
};

// Optional metadata.insufficient_data_breakout — explains WHY payers landed
// in the "Insufficient Data" tier, with the payers behind each reason.
type BreakoutPayer = {
  payer?: string;
  volume?: number;
  pa_required_pct?: number;
  decided?: number;
  pa_undecided?: number;
};

type BreakoutReason = {
  reason?: string;
  description?: string;
  payer_count?: number;
  pct_of_top_n?: number;
  pct_of_insufficient?: number;
  payers?: BreakoutPayer[];
};

type Breakout = {
  payer_count?: number;
  pct_of_top_n?: number;
  sub_reasons?: BreakoutReason[];
};

const getBreakout = (meta?: Record<string, unknown>): Breakout | null => {
  const b = meta?.insufficient_data_breakout;
  if (!b || typeof b !== "object") return null;
  const breakout = b as Breakout;
  if (!Array.isArray(breakout.sub_reasons) || breakout.sub_reasons.length === 0)
    return null;
  return breakout;
};

type SortState = { col: string; dir: "asc" | "desc" } | null;

const BAR_COLORS = [
  "#c0392b",
  "#1a7a7a",
  "#1e3a6e",
  "#565CA8",
  "#c4922a",
  "#048953",
  "#EF4B30",
  "#22324C",
  "#8e44ad",
  "#d35400",
];

const STACKED_COLORS = ["#059669", "#dc2626", "#2563eb", "#d97706"];

// Categorical colors for the insufficient-data sub-reasons. Hue-separated for
// CVD safety; the reason text remains the primary encoding. `bar` colors the
// dot/share bar (graphics need ≥3:1); `text` is a darker variant that clears
// the 4.5:1 floor for the small % value.
const REASON_COLORS: Array<{ bar: string; text: string }> = [
  { bar: "#2563eb", text: "#1d4ed8" },
  { bar: "#d97706", text: "#b45309" },
  { bar: "#0d9488", text: "#0f766e" },
  { bar: "#7c3aed", text: "#6d28d9" },
  { bar: "#db2777", text: "#be185d" },
];

const isNumeric = (val: unknown): val is number =>
  typeof val === "number" && !isNaN(val);

type InfoBarEnvelope = {
  data?: DataRow[];
  metadata?: Record<string, unknown>;
  has_download_options?: boolean;
  download_options?: WidgetDownloadOptions;
};

const unwrap = (raw: unknown): InfoBarEnvelope => {
  if (!raw || typeof raw !== "object") return {};
  const obj = raw as Record<string, unknown>;
  if (Array.isArray(obj.data)) return obj as InfoBarEnvelope;
  if (Array.isArray(obj)) return { data: obj as DataRow[] };
  return obj as InfoBarEnvelope;
};

const getRateColor = (val: number): string => {
  if (val >= 70) return "#16a34a";
  if (val >= 40) return "#d97706";
  return "#dc2626";
};

const formatNum = (val: unknown): string => {
  if (!isNumeric(val)) return String(val ?? "–");
  return val % 1 === 0 ? val.toLocaleString() : val.toFixed(1);
};

const shortRateName = (col: string): string =>
  col.replace(/\s*(Rate|%|\(%\)|Pct)$/i, "").trim();

function compareValues(a: unknown, b: unknown, dir: "asc" | "desc"): number {
  const m = dir === "asc" ? 1 : -1;
  if (isNumeric(a) && isNumeric(b)) return (a - b) * m;
  return String(a ?? "").localeCompare(String(b ?? "")) * m;
}

function SortIcon({ col, sort }: { col: string; sort: SortState }) {
  const active = sort?.col === col;
  if (!active) return null;
  return (
    <span
      style={{
        display: "inline-block",
        marginLeft: 3,
        fontSize: 8,
      }}
    >
      {sort.dir === "asc" ? "▲" : "▼"}
    </span>
  );
}

export default function InfoBarWidget({
  isLoading,
  data,
  title,
  dashboardName,
  widgetTemplateId,
  appliedFilters,
}: InfoBarWidgetProps): JSX.Element {
  const parsed = useMemo(() => unwrap(data), [data]);
  const rows = useMemo(() => parsed.data ?? [], [parsed]);
  const meta = parsed.metadata;
  const [sort, setSort] = useState<SortState>(null);
  const [showBreakout, setShowBreakout] = useState(false);

  const downloadMenu = (
    <DownloadOptionsMenu
      menuId={
        widgetTemplateId !== undefined ? String(widgetTemplateId) : undefined
      }
      title={title}
      dashboardName={dashboardName}
      widgetTemplateId={widgetTemplateId}
      appliedFilters={appliedFilters}
      downloadOptions={
        parsed.has_download_options ? parsed.download_options : undefined
      }
    />
  );

  const breakout = getBreakout(meta);

  const columns = useMemo(() => {
    if (rows.length === 0) return [];
    return Object.keys(rows[0] ?? {});
  }, [rows]);

  const labelCol = useMemo(
    () =>
      columns.find((c) => rows.every((r) => !isNumeric(r[c]))) ??
      columns[0] ??
      "",
    [columns, rows],
  );

  const rateCols = useMemo(
    () =>
      columns.filter(
        (c) =>
          c !== labelCol &&
          (c.toLowerCase().includes("rate") || c.includes("%")) &&
          rows.some((r) => isNumeric(r[c])),
      ),
    [columns, rows, labelCol],
  );

  const isStacked = rateCols.length > 1;

  const leadCols = useMemo(
    () =>
      columns.filter(
        (c) =>
          c !== labelCol &&
          !rateCols.includes(c) &&
          c.toLowerCase().includes("vol") &&
          rows.some((r) => isNumeric(r[c])),
      ),
    [columns, rows, labelCol, rateCols],
  );

  const extraCols = useMemo(
    () =>
      columns.filter(
        (c) =>
          c !== labelCol &&
          !rateCols.includes(c) &&
          !leadCols.includes(c) &&
          rows.some((r) => isNumeric(r[c])),
      ),
    [columns, rows, labelCol, rateCols, leadCols],
  );

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    return [...rows].sort((a, b) =>
      compareValues(a[sort.col], b[sort.col], sort.dir),
    );
  }, [rows, sort]);

  const toggleSort = (col: string) => {
    setSort((prev) => {
      if (prev?.col === col) {
        return prev.dir === "desc" ? { col, dir: "asc" } : null;
      }
      return { col, dir: "desc" };
    });
  };

  const headerStyle: React.CSSProperties = {
    cursor: "pointer",
    userSelect: "none",
  };

  if (isLoading) {
    return (
      <div className="placeholder-glow h-100">
        <span className="placeholder w-100 h-100" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <>
        {downloadMenu}
        <p className="text-muted small mb-0">No data available.</p>
      </>
    );
  }

  return (
    <div className="info-bar">
      {downloadMenu}
      {meta && Object.keys(meta).length > 0 && (
        <div className="info-bar__meta">
          {Object.entries(meta).map(([key, val]) => {
            if (typeof val === "string") {
              // Long prose (e.g. tier threshold definitions) doesn't fit a
              // chip — show a compact ⓘ chip with the text as a tooltip.
              if (val.length > 60) {
                return (
                  <span
                    key={key}
                    className="info-bar__meta-stat"
                    title={val}
                    style={{ cursor: "help" }}
                  >
                    <i className="bi bi-info-circle me-1" aria-hidden="true" />
                    {key.replace(/_/g, " ")}
                  </span>
                );
              }
              return (
                <span key={key} className="info-bar__meta-stat">
                  {key.replace(/_/g, " ")}: {val}
                </span>
              );
            }
            if (isNumeric(val)) {
              const label = key.replace(/^total_/, "").replace(/_/g, " ");
              const display = key.includes("pct")
                ? `${val.toFixed(1)}%`
                : val.toLocaleString();
              return (
                <span key={key} className="info-bar__meta-stat">
                  {label}: {display}
                </span>
              );
            }
            return null;
          })}
        </div>
      )}

      <div className="info-bar__header">
        <span
          className="info-bar__header-label"
          style={headerStyle}
          onClick={() => toggleSort(labelCol)}
        >
          {labelCol}
          <SortIcon col={labelCol} sort={sort} />
        </span>
        {rateCols.length > 0 && (
          <span className="info-bar__header-bar">
            {isStacked ? "Distribution" : rateCols[0]}
          </span>
        )}
        {leadCols.map((col) => (
          <span
            key={col}
            className="info-bar__header-extra"
            style={headerStyle}
            onClick={() => toggleSort(col)}
          >
            {col}
            <SortIcon col={col} sort={sort} />
          </span>
        ))}
        {rateCols.map((col, ci) => (
          <span
            key={col}
            className="info-bar__header-rate"
            style={{
              ...headerStyle,
              ...(isStacked
                ? { color: STACKED_COLORS[ci % STACKED_COLORS.length] }
                : {}),
            }}
            onClick={() => toggleSort(col)}
          >
            {isStacked ? shortRateName(col) : "Rate"}
            <SortIcon col={col} sort={sort} />
          </span>
        ))}
        {extraCols.map((col) => (
          <span
            key={col}
            className="info-bar__header-extra"
            style={headerStyle}
            onClick={() => toggleSort(col)}
          >
            {col}
            <SortIcon col={col} sort={sort} />
          </span>
        ))}
      </div>

      <div className="info-bar__list">
        {sortedRows.map((row, idx) => (
          <div key={idx} className="info-bar__row">
            <span className="info-bar__label">
              {String(row[labelCol] ?? "")}
            </span>

            {rateCols.length > 0 && (
              <span
                className={`info-bar__bar${isStacked ? " info-bar__bar--stacked" : ""}`}
              >
                {isStacked ? (
                  rateCols.map((col, ci) => {
                    const val = isNumeric(row[col]) ? (row[col] as number) : 0;
                    return (
                      <span
                        key={col}
                        className="info-bar__bar-segment"
                        style={{
                          flexGrow: val,
                          background:
                            STACKED_COLORS[ci % STACKED_COLORS.length],
                        }}
                      />
                    );
                  })
                ) : (
                  <span
                    className="info-bar__bar-fill"
                    style={{
                      width: `${Math.min(
                        isNumeric(row[rateCols[0] ?? ""])
                          ? (row[rateCols[0] ?? ""] as number)
                          : 0,
                        100,
                      )}%`,
                      background: BAR_COLORS[idx % BAR_COLORS.length],
                    }}
                  />
                )}
              </span>
            )}

            {leadCols.map((col) => (
              <span key={col} className="info-bar__extra">
                {formatNum(row[col])}
              </span>
            ))}

            {rateCols.map((col, ci) => {
              const val = isNumeric(row[col]) ? (row[col] as number) : 0;
              return (
                <span
                  key={col}
                  className="info-bar__rate"
                  style={{
                    color: isStacked
                      ? STACKED_COLORS[ci % STACKED_COLORS.length]
                      : getRateColor(val),
                  }}
                >
                  {formatNum(val)}%
                </span>
              );
            })}

            {extraCols.map((col) => (
              <span key={col} className="info-bar__extra">
                {formatNum(row[col])}
              </span>
            ))}
          </div>
        ))}
      </div>

      {breakout && (
        <div className="info-bar__breakout">
          <button
            type="button"
            className="info-bar__breakout-toggle"
            aria-expanded={showBreakout}
            onClick={() => setShowBreakout((v) => !v)}
          >
            <i
              className={`bi bi-chevron-${showBreakout ? "down" : "right"}`}
              aria-hidden="true"
            />
            <span>Why insufficient data?</span>
            <span className="info-bar__breakout-summary">
              {breakout.payer_count ?? 0} payer
              {(breakout.payer_count ?? 0) !== 1 ? "s" : ""}
              {isNumeric(breakout.pct_of_top_n)
                ? ` · ${formatNum(breakout.pct_of_top_n)}% of top ${
                    isNumeric(meta?.top_n) ? meta.top_n : "N"
                  }`
                : ""}
            </span>
          </button>

          {showBreakout && (
            <div className="info-bar__breakout-body">
              {(breakout.sub_reasons ?? []).map((r, ri) => {
                const count = r.payer_count ?? 0;
                const isEmpty = count === 0;
                const share = isNumeric(r.pct_of_insufficient)
                  ? r.pct_of_insufficient
                  : 0;
                const color =
                  REASON_COLORS[ri % REASON_COLORS.length] ??
                  REASON_COLORS[0]!;
                return (
                  <div
                    key={r.reason ?? ri}
                    className={`info-bar__reason${isEmpty ? " info-bar__reason--empty" : ""}`}
                  >
                    <div className="info-bar__reason-head">
                      <span className="info-bar__reason-name">
                        <span
                          className="info-bar__reason-dot"
                          style={{ background: color.bar }}
                          aria-hidden="true"
                        />
                        {r.reason ?? "—"}
                      </span>
                      <span className="info-bar__reason-track">
                        <span
                          className="info-bar__reason-fill"
                          style={{
                            width: `${Math.min(share, 100)}%`,
                            background: color.bar,
                          }}
                        />
                      </span>
                      <span className="info-bar__reason-stats">
                        {count} payer{count !== 1 ? "s" : ""}
                        {!isEmpty && (
                          <>
                            {" · "}
                            <span style={{ color: color.text, fontWeight: 600 }}>
                              {formatNum(share)}%
                            </span>
                          </>
                        )}
                      </span>
                    </div>

                    {!isEmpty && r.description && (
                      <p className="info-bar__reason-desc">{r.description}</p>
                    )}

                    {!isEmpty && (r.payers?.length ?? 0) > 0 && (
                      <table className="info-bar__payer-table">
                        <thead>
                          <tr>
                            <th>Payer</th>
                            <th>Volume</th>
                            <th>PA Req %</th>
                            <th>Decided</th>
                            <th>Undecided</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(r.payers ?? []).map((p, pi) => (
                            <tr key={p.payer ?? pi}>
                              <td title={p.payer}>{p.payer ?? "—"}</td>
                              <td>{formatNum(p.volume)}</td>
                              <td>
                                {isNumeric(p.pa_required_pct)
                                  ? `${formatNum(p.pa_required_pct)}%`
                                  : "–"}
                              </td>
                              <td>{formatNum(p.decided)}</td>
                              <td>{formatNum(p.pa_undecided)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
